import React, { memo, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import apiService from "../../../services/apiService";
import { crmColors } from "../../../theme/colors";

const WA = crmColors.whatsapp;

const formatMoney = (n, digits = 2) =>
  `₹${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;

const InfoRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} textAlign="right">
      {value ?? "—"}
    </Typography>
  </Stack>
);

/**
 * Spec §5.1 — school detail drawer (Phase 1 view-only tabs).
 */
const WhatsAppSchoolDetailDrawer = ({ open, schoolCode, onClose }) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !schoolCode) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setTab(0);
      const result = await apiService.getWhatsAppAdminSchoolDetail(schoolCode);
      if (cancelled) return;
      if (result.success) {
        setDetail(result.data || null);
      } else {
        setDetail(null);
        toast.error(result.error || "Failed to load school detail");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, schoolCode]);

  const account = detail?.account || {};
  const school = detail?.school || {};
  const subscription = detail?.subscription || null;
  const wallet = detail?.wallet || null;
  const templates = Array.isArray(detail?.templates) ? detail.templates : [];
  const messageRows = Array.isArray(detail?.message_log?.rows) ? detail.message_log.rows : [];
  const walletRows = Array.isArray(detail?.wallet_transactions?.rows)
    ? detail.wallet_transactions.rows
    : [];

  const quality = String(account.quality_rating || "").toUpperCase();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 480, md: 560 }, maxWidth: "100%" },
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(WA, 0.15)}`, bgcolor: alpha(WA, 0.06) }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              {school.school_name || schoolCode || "School"}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {schoolCode}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}
      >
        <Tab label="Account" sx={{ textTransform: "none", fontWeight: 700 }} />
        <Tab label="Subscription" sx={{ textTransform: "none", fontWeight: 700 }} />
        <Tab label="Wallet" sx={{ textTransform: "none", fontWeight: 700 }} />
        <Tab label="Templates" sx={{ textTransform: "none", fontWeight: 700 }} />
        <Tab label="Messages" sx={{ textTransform: "none", fontWeight: 700 }} />
      </Tabs>

      <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: WA }} />
          </Box>
        ) : !detail ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            No detail available.
          </Typography>
        ) : tab === 0 ? (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={account.connection_status || "—"}
                sx={{ fontWeight: 700, textTransform: "capitalize" }}
              />
              {quality && (
                <Chip size="small" label={`Quality ${quality}`} sx={{ fontWeight: 700 }} />
              )}
              <Chip
                size="small"
                label={`Service ${detail.service_status || detail.service?.status || "InActive"}`}
                color={detail.service_active ? "success" : "default"}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
            <InfoRow label="Verified name" value={account.verified_name} />
            <InfoRow label="Display phone" value={account.display_phone_number} />
            <InfoRow label="WABA ID" value={account.waba_id} />
            <InfoRow label="Phone number ID" value={account.phone_number_id} />
            <InfoRow label="Business ID" value={account.business_id} />
            <InfoRow label="Messaging tier" value={account.messaging_tier} />
            <InfoRow label="Signup source" value={account.signup_source} />
            <InfoRow label="Onboarded at" value={account.onboarded_at} />
            <InfoRow label="Meta cost this month" value={formatMoney(detail.meta_cost_this_month, 4)} />
            <InfoRow label="Last message" value={detail.last_message_at || "—"} />

            <Divider sx={{ my: 1.75 }} />
            <Typography fontWeight={800} sx={{ mb: 1 }}>
              WhatsApp school service
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              From tm_whatsapp_school_service
            </Typography>
            {!detail.service ? (
              <Typography color="text.secondary" variant="body2">
                No service record for this school.
              </Typography>
            ) : (
              <>
                <InfoRow label="Status" value={detail.service.status || detail.service_status || "—"} />
                <InfoRow
                  label="Effective"
                  value={detail.service_active ? "Active" : "Not active"}
                />
                <InfoRow label="Activated at" value={detail.service.activated_at || "—"} />
                <InfoRow label="Expires at" value={detail.service.expires_at || "—"} />
                <InfoRow label="Remark" value={detail.service.remark || "—"} />
                <InfoRow label="Service ID" value={detail.service.id || "—"} />
                {detail.service_message && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    {detail.service_message}
                  </Typography>
                )}
              </>
            )}
          </Box>
        ) : tab === 1 ? (
          <Box>
            {!subscription ? (
              <Typography color="text.secondary">No subscription assigned.</Typography>
            ) : (
              <>
                <InfoRow label="Plan" value={subscription.plan_name} />
                <InfoRow label="Status" value={subscription.status} />
                <InfoRow label="Plan value" value={formatMoney(subscription.plan_value ?? subscription.monthly_fee, 0)} />
                <InfoRow label="Validity" value={subscription.plan_validity || "—"} />
                <InfoRow label="Cycle start" value={subscription.cycle_start_date} />
                <InfoRow label="Cycle end" value={subscription.cycle_end_date} />
                <InfoRow
                  label="Usage"
                  value={`${Number(subscription.messages_used_this_cycle || 0).toLocaleString()} / ${Number(subscription.included_messages || 0).toLocaleString()}`}
                />
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Number(subscription.usage_percent) || 0}
                    color={subscription.in_overage ? "warning" : "success"}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                    {subscription.in_overage
                      ? "In overage"
                      : `${Number(subscription.messages_remaining || 0).toLocaleString()} remaining`}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                  Plan change & cadence override arrive in Phase 2.
                </Typography>
              </>
            )}
          </Box>
        ) : tab === 2 ? (
          <Box>
            <InfoRow label="Balance" value={formatMoney(wallet?.balance, 2)} />
            <InfoRow label="Low-balance threshold" value={formatMoney(wallet?.low_balance_threshold, 0)} />
            <Divider sx={{ my: 1.5 }} />
            <Typography fontWeight={800} sx={{ mb: 1 }}>
              Recent transactions
            </Typography>
            {walletRows.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No wallet transactions.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {walletRows.slice(0, 20).map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{tx.type}</TableCell>
                      <TableCell>{formatMoney(tx.amount, 2)}</TableCell>
                      <TableCell>{tx.created_at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              Manual wallet adjust arrives in Phase 2.
            </Typography>
          </Box>
        ) : tab === 3 ? (
          <Box>
            {templates.length === 0 ? (
              <Typography color="text.secondary">No templates for this school.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((tpl) => (
                    <TableRow key={tpl.id}>
                      <TableCell>
                        <Typography fontWeight={700} variant="body2">
                          {tpl.name}
                        </Typography>
                        {tpl.rejection_reason && (
                          <Typography variant="caption" color="error.main">
                            {tpl.rejection_reason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{tpl.category}</TableCell>
                      <TableCell>
                        <Chip size="small" label={tpl.status} sx={{ fontWeight: 700, textTransform: "capitalize" }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        ) : (
          <Box>
            {detail.message_log?.summary && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Chip size="small" label={`Total ${detail.message_log.summary.total_messages || 0}`} />
                <Chip size="small" label={`Delivered ${detail.message_log.summary.delivered_count || 0}`} color="success" variant="outlined" />
                <Chip size="small" label={`Failed ${detail.message_log.summary.failed_count || 0}`} color="error" variant="outlined" />
                <Chip size="small" label={`Cost ${formatMoney(detail.message_log.summary.total_cost, 4)}`} />
              </Stack>
            )}
            {messageRows.length === 0 ? (
              <Typography color="text.secondary">No messages in current period.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messageRows.slice(0, 50).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.recipient_number}</TableCell>
                      <TableCell>
                        <Chip size="small" label={m.status} sx={{ fontWeight: 700, textTransform: "capitalize" }} />
                      </TableCell>
                      <TableCell>{m.category}</TableCell>
                      <TableCell>{m.created_at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default memo(WhatsAppSchoolDetailDrawer);
